import { create } from 'zustand';

import { getBizyPanelMode } from '../lib/panel-preferences';

type BizySidePanelView = 'chat' | 'history';
export const BIZY_SETTINGS_SECTIONS = ['general', 'memories', 'notifications', 'connections', 'apps'] as const;
export type BizySettingsSection = (typeof BIZY_SETTINGS_SECTIONS)[number];

export type BizyPanelTrigger = 'auto' | 'user' | 'keyboard' | 'navbar_restore' | 'navbar_collapse' | (string & {});

// Set as lastTrigger by useBizySettingsDeepLink when `?bizy_settings=<section>`
// opens the panel. While it's the lastTrigger and the settings modal is still
// open, 'auto' closes no-op (see `close`) so later mount effects — the
// sessionStorage restore on Rails admin pages and the meeting-route preference
// close in useBizyLayout — can't undo the deep link. Any user interaction
// overwrites lastTrigger, ending the protection.
export const BIZY_SETTINGS_DEEP_LINK_TRIGGER = 'settings_deep_link';

// Set as lastTrigger by useBizyDeepLink when `?bizy=...` / `?bizy_prompt=...` /
// `?bizy_conversation=...` opens the panel. While it's the lastTrigger, 'auto'
// closes no-op (see `close`) so the route-aware auto-open/close effects and
// session-restore that run later in the same mount can't undo the deep link
// before the prompt/conversation is consumed. Any user interaction overwrites
// lastTrigger, ending the protection.
export const BIZY_DEEP_LINK_TRIGGER = 'deep_link';

// Set as lastTrigger by useBizyRouteSync when a `/bizy` route (or back/forward
// to one) opens the panel. Like the deep-link trigger, 'auto' closes no-op
// while it's the lastTrigger so the mount-time route-aware auto-open/close
// effects can't undo an open the URL is asking for. A user interaction
// overwrites lastTrigger, ending the protection (and re-enabling URL sync of
// user-initiated opens/closes). (BIZY-680)
export const BIZY_ROUTE_TRIGGER = 'route';

export type BizySupportMode = 'bizy' | 'fin' | 'human_agent';

export interface BizySupportContext {
	summary?: string;
	category?: string;
	finConversationId?: string;
}

interface BizySidePanelOpenOptions {
	isFullscreen?: boolean;
	isHistoryDrawerOpen?: boolean;
}

export const BIZY_PANEL_DEFAULT_WIDTH = 352;
export const BIZY_PANEL_MIN_WIDTH = 280;
export const BIZY_PANEL_MAX_WIDTH = 560;
export const BIZY_FULLSCREEN_DRAWER_DEFAULT_WIDTH = 292;
export const BIZY_FULLSCREEN_DRAWER_MIN_WIDTH = 240;
export const BIZY_FULLSCREEN_DRAWER_MAX_WIDTH = 440;

interface BizySidePanelStore {
	isOpen: boolean;
	isFullscreen: boolean;
	lastTrigger: BizyPanelTrigger | null;
	view: BizySidePanelView;
	isHistoryDrawerOpen: boolean;
	fullscreenHistoryDrawerOpen: boolean;
	isSettingsModalOpen: boolean;
	settingsSection: BizySettingsSection;
	// The chat view's active conversation id, mirrored here from the chat hook
	// (via BizySidePanel's onConversationIdChange) so URL sync can put the id in
	// navigation state without reaching into the chat view. Undefined = a
	// new/empty chat. (BIZY-680)
	activeConversationId: string | undefined;
	fullscreenDrawerWidth: number;
	width: number;
	isResizing: boolean;
	supportMode: BizySupportMode;
	supportContext: BizySupportContext;
	// Bumped by `openFromNotificationPeek` so the coordinator hook can
	// pick up an engagement signal even when the panel is already open
	// (where the close→open snapshot pattern would otherwise no-op).
	// Drives the "click Ask Bizy with unread + panel already open"
	// path: previously this just toggled the panel closed; now it surfaces
	// the proactive conversation.
	engageRequestVersion: number;
	// The People Browser entry (by person id) to highlight/scroll into view,
	// e.g. when the panel is opened from a peek pointing at an observed moment.
	// Null when nothing is focused.
	focusedPersonId: string | null;
	// The person whose (stub) detail page is currently shown in the main column
	// instead of the chat, set when a sidebar entry is clicked. Null = show chat.
	// `observedMomentId` identifies the specific moment (a person can have several),
	// so the sidebar highlights only the clicked entry. Distinct from
	// `focusedPersonId` (the peek path's person-level highlight). (BIZY-636)
	viewedPerson: { id: string; name: string; observedMomentId: string } | null;
	// Fullscreen People Browser sidebar visibility (BIZY-665). Collapsing hides
	// the left column; the person-view and chat headers show a reopen trigger.
	// Transient: resets when the panel closes or fullscreen is exited.
	isPeopleBrowserCollapsed: boolean;
	open: (trigger?: BizyPanelTrigger, options?: BizySidePanelOpenOptions) => void;
	// Opens the panel via the notification-peek path. Sets lastTrigger so
	// useLoadProactiveConversationOnOpen knows to auto-load the linked
	// proactive conversation and mark it seen. Bumps engageRequestVersion
	// so the coordinator engages even if the panel was already open.
	openFromNotificationPeek: () => void;
	close: (trigger?: BizyPanelTrigger) => void;
	toggle: (trigger?: BizyPanelTrigger) => void;
	toggleFullscreen: () => void;
	exitFullscreen: () => void;
	showChat: () => void;
	showHistory: () => void;
	showSettings: (section?: BizySettingsSection) => void;
	// Switches the active settings section without touching any other panel/modal
	// state — used for tab changes inside an already-open settings modal, so URL
	// sync can observe the selection. Unlike `showSettings`, it won't force
	// `view: 'chat'` or (re)open the modal. (BIZY-701)
	setSettingsSection: (section: BizySettingsSection) => void;
	closeHistoryDrawer: () => void;
	closeSettingsModal: () => void;
	setActiveConversationId: (conversationId: string | undefined) => void;
	setFullscreenDrawerWidth: (width: number) => void;
	setWidth: (width: number) => void;
	setIsResizing: (isResizing: boolean) => void;
	setSupportMode: (mode: BizySupportMode) => void;
	setSupportContext: (context: BizySupportContext) => void;
	resetSupport: () => void;
	// Focuses a People Browser entry (ensuring the panel is open on the chat
	// view where the sidebar lives). Used by the peek path when the latest
	// unread is an observed moment rather than a conversation.
	focusPerson: (personId: string | null) => void;
	// Opens a person's (stub) detail page in the main column. (BIZY-636)
	viewPerson: (person: { id: string; name: string; observedMomentId: string }) => void;
	// Clears the viewed person, returning the main column to the chat.
	clearViewedPerson: () => void;
	togglePeopleBrowserCollapsed: () => void;
}

export const useBizySidePanelStore = create<BizySidePanelStore>((set) => ({
	isOpen: false,
	isFullscreen: false,
	lastTrigger: null,
	view: 'chat',
	isHistoryDrawerOpen: false,
	fullscreenHistoryDrawerOpen: true,
	isSettingsModalOpen: false,
	settingsSection: 'general',
	activeConversationId: undefined,
	fullscreenDrawerWidth: BIZY_FULLSCREEN_DRAWER_DEFAULT_WIDTH,
	width: BIZY_PANEL_DEFAULT_WIDTH,
	isResizing: false,
	supportMode: 'bizy',
	supportContext: {},
	engageRequestVersion: 0,
	focusedPersonId: null,
	viewedPerson: null,
	isPeopleBrowserCollapsed: false,
	open: (trigger, options) =>
		set((state) => {
			const panelMode = getBizyPanelMode();
			const isFullscreen = options?.isFullscreen ?? panelMode === 'fullscreen';
			return {
				isOpen: true,
				isFullscreen,
				isHistoryDrawerOpen: isFullscreen ? (options?.isHistoryDrawerOpen ?? state.fullscreenHistoryDrawerOpen) : false,
				viewedPerson: null,
				lastTrigger: trigger ?? 'user',
			};
		}),
	// Force view: 'chat' so the coordinator hook (which lives in
	// BizySidePanelChatView) actually mounts. Without this, a user who left
	// the panel on history/settings and closed it would re-open into that
	// stale view via peek and never see the proactive conversation surface.
	openFromNotificationPeek: () =>
		set((state) => {
			const isFullscreen = getBizyPanelMode() === 'fullscreen';
			return {
				isOpen: true,
				isFullscreen,
				lastTrigger: 'notification_peek',
				view: 'chat',
				isHistoryDrawerOpen: isFullscreen ? state.fullscreenHistoryDrawerOpen : false,
				isSettingsModalOpen: false,
				// Clear any open person stub so the proactive conversation the peek
				// engages isn't hidden behind the person-view overlay.
				viewedPerson: null,
				engageRequestVersion: state.engageRequestVersion + 1,
			};
		}),
	close: (trigger) =>
		set((state) => {
			// Keep a settings deep link (?bizy_settings=...) open through 'auto'
			// close/restore effects that run later in the same mount.
			if (trigger === 'auto' && state.isSettingsModalOpen && state.lastTrigger === BIZY_SETTINGS_DEEP_LINK_TRIGGER) return state;
			// Same protection for a chat deep link (?bizy_prompt=..., ?bizy_conversation=...):
			// the route-aware auto-open/close effects must not undo the deep-link
			// open before the pending prompt/conversation is consumed.
			if (trigger === 'auto' && state.lastTrigger === BIZY_DEEP_LINK_TRIGGER) return state;
			// Same protection for a `/bizy` route open (BIZY-680): the mount-time
			// auto-open/close effects must not close a panel the URL is asking to open.
			if (trigger === 'auto' && state.lastTrigger === BIZY_ROUTE_TRIGGER) return state;
			return {
				isOpen: false,
				isHistoryDrawerOpen: false,
				isSettingsModalOpen: false,
				viewedPerson: null,
				isPeopleBrowserCollapsed: false,
				lastTrigger: trigger ?? 'user',
			};
		}),
	toggle: (trigger) =>
		set((state) => {
			const isOpening = !state.isOpen;
			const isFullscreen = state.isOpen ? state.isFullscreen : getBizyPanelMode() === 'fullscreen';
			return {
				isOpen: isOpening,
				isFullscreen,
				isHistoryDrawerOpen: isOpening && isFullscreen ? state.fullscreenHistoryDrawerOpen : false,
				isSettingsModalOpen: state.isOpen ? false : state.isSettingsModalOpen,
				viewedPerson: null,
				isPeopleBrowserCollapsed: state.isOpen ? false : state.isPeopleBrowserCollapsed,
				lastTrigger: trigger ?? 'user',
			};
		}),
	toggleFullscreen: () =>
		set((state) => {
			const next = !state.isFullscreen;
			// Sidebar collapse is transient per fullscreen visit; leaving
			// fullscreen resets it so the next visit starts with the sidebar.
			return {
				isFullscreen: next,
				isHistoryDrawerOpen: next ? state.fullscreenHistoryDrawerOpen : false,
				isPeopleBrowserCollapsed: next ? state.isPeopleBrowserCollapsed : false,
			};
		}),
	exitFullscreen: () => set({ isFullscreen: false, isHistoryDrawerOpen: false, isPeopleBrowserCollapsed: false }),
	showChat: () =>
		set((state) => ({
			view: 'chat',
			isHistoryDrawerOpen: state.isFullscreen ? state.isHistoryDrawerOpen : false,
			isSettingsModalOpen: false,
			viewedPerson: null,
		})),
	showHistory: () => set({ view: 'chat', isHistoryDrawerOpen: true, fullscreenHistoryDrawerOpen: true, isSettingsModalOpen: false }),
	showSettings: (settingsSection = 'general') => set({ view: 'chat', isSettingsModalOpen: true, settingsSection }),
	setSettingsSection: (settingsSection) => set({ settingsSection }),
	closeHistoryDrawer: () =>
		set((state) => ({ isHistoryDrawerOpen: false, fullscreenHistoryDrawerOpen: state.isFullscreen ? false : state.fullscreenHistoryDrawerOpen })),
	closeSettingsModal: () => set({ isSettingsModalOpen: false }),
	setActiveConversationId: (activeConversationId) => set({ activeConversationId }),
	setFullscreenDrawerWidth: (width) =>
		set({ fullscreenDrawerWidth: Math.max(BIZY_FULLSCREEN_DRAWER_MIN_WIDTH, Math.min(BIZY_FULLSCREEN_DRAWER_MAX_WIDTH, width)) }),
	setWidth: (width) => set({ width: Math.max(BIZY_PANEL_MIN_WIDTH, Math.min(BIZY_PANEL_MAX_WIDTH, width)) }),
	setIsResizing: (isResizing) => set({ isResizing }),
	setSupportMode: (supportMode) => set({ supportMode }),
	setSupportContext: (supportContext) => set({ supportContext }),
	resetSupport: () => set({ supportMode: 'bizy', supportContext: {} }),
	// Ensure the panel is open on the chat view (where the People Browser
	// left column lives) and highlight the given entry. isSettingsModalOpen is
	// cleared so the sidebar is actually visible.
	// Person-level sidebar highlight for the peek path. Clears any open person
	// stub so the highlighted entry / engaged conversation is visible.
	focusPerson: (personId) => set({ isOpen: true, view: 'chat', isSettingsModalOpen: false, focusedPersonId: personId, viewedPerson: null }),
	// Opens a specific moment's person stub. Highlight is keyed on the moment
	// (via viewedPerson), so this does not set the person-level focusedPersonId.
	viewPerson: (person) => set({ isOpen: true, view: 'chat', isSettingsModalOpen: false, viewedPerson: person }),
	clearViewedPerson: () => set({ viewedPerson: null }),
	togglePeopleBrowserCollapsed: () => set((state) => ({ isPeopleBrowserCollapsed: !state.isPeopleBrowserCollapsed })),
}));
